/* eslint-disable @typescript-eslint/ban-ts-comment,arrow-body-style,@typescript-eslint/member-ordering */
// noinspection JSMethodCanBeStatic

import React from "react";
import {observer} from "mobx-react";
import {autorun, computed} from "mobx";

import {Button, Dimmer, Icon, Label, Loader, Message, Popup, Transition} from "semantic-ui-react";

import * as d3 from "d3";
import {BaseType, TimeLocaleObject} from "d3";

import "./chart.scss";

import {Kiwis, GeneralisationStatus, StatusType, TimeSeries} from "../../../../../../kiwis";
import {ChartConfiguration, ThresholdsConfiguration} from "../../../../../../config/chart-configuration";
import {ChartTools} from "./chart-tools/chart-tools";
import {TimeSeriesRangeSelector} from "./time-series-range-selector/time-series-range-selector";
import {UnitsUtils} from "../../../../../../utils/units-utils";
import "dayjs/locale/de-ch";
import {DataValueType} from "../../../../../../kiwis/data-value-type";
import {Thresholds} from "./thresholds/thresholds";
import dayjs = require("dayjs");
import {SelectOptionConfiguration} from "../../../../../../core/config/select-option-configuration";

interface ChartProps {
    kiwis: Kiwis;
    config: ChartConfiguration;
    timeSeries: TimeSeries;
    visible: boolean;
    parentWidth: number;
    parentHeight: number;
    aspectRatio: number; // aspect ratio of parent windows
}

dayjs.locale("de-ch");

interface RefreshButtonProps {
    kiwis: Kiwis;
    timeSeries: TimeSeries;
}

const RefreshButton = (props: RefreshButtonProps) => (
    <Popup
        content="Daten neu laden" basic
        className="popup-hints"
        trigger={
            <Button
                icon="refresh" size="mini"
                label="Neu laden"
                className="graph-refresh"
                onClick={() => {
                    props.kiwis.loadTimeSeriesData(props.timeSeries.id, true);
                }}
            />}
    />
);

@observer
export class Chart extends React.Component<ChartProps, any> {

    /**
     * If time series possibly has relative values
     */
    @computed get hasRelativeData() {
        // only show relative values when parameter type is configured for relative values and
        // flag is set to show relative values
        return this.props.kiwis.hasRelativeValues(this.props.timeSeries.parameterTypeName);
    }

    /**
     * If to show absolute values
     * Only show relative data when
     */
    @computed get showRelativeData() {
        return this.props.kiwis.dataValuesType === DataValueType.Relative &&
            this.props.timeSeries.dataRelativeValueIndex != null;
    }

    /**
     * Position of data value depends on time series configurations in WISKI.
     */
    @computed get dataValueIndex(): 1 | 2 | -1 {
        if (this.showRelativeData) {
            if (this.props.timeSeries.dataRelativeValueIndex) {
                return this.props.timeSeries.dataRelativeValueIndex === 1 ? 1 : 2;
            } else {
                // no relative data
                return -1;
            }
        } else {
            if (this.props.timeSeries.dataAbsoluteValueIndex) {
                return this.props.timeSeries.dataAbsoluteValueIndex === 1 ? 1 : 2;
            } else {
                // no absolute data
                return -1;
            }
        }
    }

    get useBarChart() {
        return this.props.config.barCharts.includes(this.props.timeSeries.stationParameterName);
    }

    get hasCumulativeCurve() {
        return this.props.config.cumulativeValue.includes(this.props.timeSeries.stationParameterName);
    }

    // timeSeries is only observed when computed and used as reference
    @computed
    get timeSeriesData(): TimeSeriesDataTuple[] {
        const ts = this.props.timeSeries;
        const data = this.props.kiwis.timeSeriesData.get(ts.id);
        return data ? data.slice() : [["", -999, -999]];
    }

    @computed
    get chartContentLabel(): string | undefined {
        if (!this.props.config.axes || !this.props.config.axes.xAxis) {
            // eslint-disable-next-line no-console
            console.error("Keine Labels für X-Achse konfiguriert.");
            return "";
        }
        const period = this.props.timeSeries.periodISO || this.props.timeSeries.DEFAULT_PERIOD;

        const labelsConfig = this.props.config.axes.xAxis.labels.filter((lc) => {
            return lc.parameterTypes.includes(this.props.timeSeries.parameterTypeName);
        })[0];

        // normalize type
        const types = ["total", "aggtotal"];
        const type = types.indexOf(this.props.timeSeries.typeName) !== -1 ? this.props.timeSeries.typeName : "mean";

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        let label = labelsConfig[type][period];

        if (!label) {
            return "";
        }

        if (this.props.parentWidth < 550) {
            label = `${label.substring(0, 26)}...`;
        }

        return label;
    }

    @computed
    get dataCoverageLabel() {
        const from = dayjs(new Date(this.props.timeSeries.coverage.from)).format("DD.MM.YYYY");
        const to = dayjs(new Date(this.props.timeSeries.coverage.to)).format("DD.MM.YYYY");
        return `Daten verfügbar: ${from} - ${to}`;
    }

    @computed
    get dateRangeLabel(): string | undefined {
        const from = this.props.timeSeries.dataRequestedFromDate || this.xExtent[0];
        const to = this.props.timeSeries.dataRequestedToDate || this.xExtent[1];
        return `${dayjs(from).format("DD.MM.YYYY")} - ${dayjs(to).format("DD.MM.YYYY")}`;
    }

    @computed
    get yAxisLabel(): string | undefined {
        const ts = this.props.kiwis.timeSeriesList.get(this.props.timeSeries.id);
        const valueType = this.showRelativeData ? DataValueType.Relative : DataValueType.Absolute;
        if (ts) {
            return `${ts.parameterTypeName} [${UnitsUtils.unitLabel(ts.unitSymbol, valueType)}]`;
        }
    }

    @computed
    get yCumulativeAxisLabel(): string | undefined {
        const ts = this.props.kiwis.timeSeriesList.get(this.props.timeSeries.id);
        const valueType = this.showRelativeData ? DataValueType.Relative : DataValueType.Absolute;
        if (!ts) {
            return "";
        }
        if (this.props.parentHeight > 480) {
            return `${ts.parameterTypeName} Summe [${UnitsUtils.unitLabel(ts.unitSymbol, valueType)}]`;
        } else {
            return `Summe [${UnitsUtils.unitLabel(ts.unitSymbol, valueType)}]`;
        }
    }

    @computed
    get xAxisLastValueLabel(): string | undefined {
        return `Aktuellster Wert: , ${dayjs(this.xExtent[1] as Date).format("DD.MM.YYYY HH:mm")}`;
    }

    /**
     * Data (Date) extent for X value
     */
    @computed
    get xExtent(): Date[] {
        // @ts-ignore
        return d3.extent(this.timeSeriesData, (d) => Date.parse(d[0]));
    }

    /**
     * Date extent for x-axis, corrected for bars to avoid overlapping
     */
    @computed
    get xExtentFixed(): Date[] {
        const extent = this.xExtent;

        if (this.useBarChart) {
            return this.offsetDateExtent(extent);
        }
        return extent;
    }

    /**
     * Data extent for Y values
     */
    @computed
    get yExtent(): Array<number | { valueOf(): number }> {
        const valueIndex = this.dataValueIndex;
        if (valueIndex === -1) {
            throw new Error("Invalid data value index. No data available for specified dataValueType.");
        }

        // calc extent of data
        const ext = d3.extent(this.timeSeriesData, (d) => d[valueIndex]);

        if (!ext[0]) {
            ext[0] = 0;
        }
        if (!ext[1]) {
            ext[1] = 10;
        }

        // handle cases when all data values are 0 --> extent would be [0,0]
        if (ext[0] === 0 && ext[0] === ext[1]) {
            // override y max value to scale the y axis nicely
            ext[1] = 10;
        }

        if (this.props.kiwis.dataValuesType !== DataValueType.Relative) {
            const {min, max} = this.getMinMaxThresholds();

            // @ts-ignore
            if (min != null && min < ext[0]) {
                ext[0] = min;
            }
            // @ts-ignore
            if (max != null && max > ext[1]) {
                ext[1] = max;
            }
        }

        // @ts-ignore
        return ext;
    }

    /**
     * Data extent for Y cumulative values
     */
    @computed
    get yExtentCumulative(): Array<number | { valueOf(): number }> {
        const valueIndex = this.dataValueIndex;
        if (valueIndex === -1) {
            throw new Error("Invalid data value index. No data available for specified dataValueType.");
        }
        const data = this.cumulativeData(this.timeSeriesData, valueIndex);
        if (!data) {
            return [0, 10];
        }

        const ext = d3.extent(data, (d) => d[valueIndex]);

        // handle cases when all data values are 0 --> extent would be [0,0]
        if (ext[0] === 0 && ext[0] === ext[1]) {
            // override y max value to scale the y axis nicely
            ext[1] = 10;
        }
        // @ts-ignore
        return ext;
    }

    get curve() {
        return d3.line()
            .curve(d3.curveMonotoneX)
            // @ts-ignore
            .defined((d) => {
                return d[0] && d[1] != null && !isNaN(d[1]);
            })
            // @ts-ignore
            .x((d) => this.x()(Date.parse(d[0])))
            // @ts-ignore
            .y((d) => this.y()(d[this.dataValueIndex]));
    }

    get lineCumulative() {
        return d3.line()
            .curve(d3.curveMonotoneX)
            // @ts-ignore
            .defined((d) => {
                return d[0] && d[1] != null && !isNaN(d[1]);
            })
            // @ts-ignore
            .x((d) => this.x()(Date.parse(d[0])))
            // @ts-ignore
            .y((d) => this.yCumulative()(d[this.dataValueIndex]));
    }

    get lineStraight() {
        return d3.line()
            // @ts-ignore
            .x((d) => this.x()(Date.parse(d[0])))
            // @ts-ignore
            .y((d) => this.y()(d[1]));
    }

    disposeRedrawOnDataChange: any;
    graphDiv: any;
    timeLocale: TimeLocaleObject;

    bisectDate = d3.bisector((d: TimeSeriesDataTuple) => {
        return Date.parse(d[0]);
    }).left;

    constructor(props: ChartProps) {
        super(props);

        this.state = {
            dataPointsVisible: false
        };

        this.graphDiv = React.createRef();
        this.timeLocale = d3.timeFormatDefaultLocale(this.props.config.defaultTimeLocale);

        this.x = this.x.bind(this);
        this.y = this.y.bind(this);
        this.yCumulative = this.yCumulative.bind(this);
        this.xAxis = this.xAxis.bind(this);
        this.yAxis = this.yAxis.bind(this);
        this.yCumulativeAxis = this.yCumulativeAxis.bind(this);
        this.xGridLines = this.xGridLines.bind(this);
        this.yGridLines = this.yGridLines.bind(this);
        this.yCumulativeGridLines = this.yCumulativeGridLines.bind(this);
        this.toggleDataPointVisibility = this.toggleDataPointVisibility.bind(this);
    }

    extractLastData(data: TimeSeriesDataTuple[]) {
        if (this.dataValueIndex === -1) {
            // no absolute or relative value
            return null;
        }
        const cm = data[data.length - 1];
        const time = Date.parse(cm[0]);
        const value = cm[this.dataValueIndex];
        return { time, value };
    }

    /**
     * Toggle the circles marking the data points in graph
     */
    toggleDataPointVisibility() {
        this.setState((state: any) => ({
            dataPointsVisible: !state.dataPointsVisible
        }));
    }

    /**
     * Return label with most up to date measurement value, including date and units
     *
     * @param data the data array
     * @param ts time series
     */
    currentMeasurementLabel(data: TimeSeriesDataTuple[], ts: TimeSeries) {
        const lastData = this.extractLastData(data);
        if (lastData === null) {
            if (this.props.parentWidth > 550) {
                return "Aktuellster Wert: -";
            } else {
                return "-";
            }
        }

        const { time, value } = lastData;
        const valueType = this.showRelativeData ? DataValueType.Relative : DataValueType.Absolute;

        // @ts-ignore
        const formattedTime = this.formattedTime(ts, time);

        let valueFixed;
        if (value) {
            // @ts-ignore
            valueFixed = value.toFixed(3);
        } else {
            valueFixed = 0;
        }
        const valueFormated = `${valueFixed} ${UnitsUtils.unitLabel(ts.unitSymbol, valueType)}`;
        if (this.props.parentWidth > 550) {
            return `Aktuellster Wert: ${valueFormated}, ${formattedTime}`;
        } else {
            return `${valueFormated}, ${formattedTime}`;
        }
    }

    /**
     * View box for SVG
     */
    viewBox() {
        const node = this.graphDiv.current;
        return `0 0 ${node.clientWidth} ${node.clientHeight}`;
    }

    /**
     * X value scaling
     */
    x() {
        const margins = this.hasCumulativeCurve ? this.props.config.marginTwoAxis : this.props.config.margin;
        const {left, right} = margins;

        return d3.scaleTime()
            .domain(this.xExtentFixed)
            .range([
                left,
                this.graphDiv.current.clientWidth - right
            ]);
    }

    /**
     * Y value scaling
     */
    y() {
        const margins = this.hasCumulativeCurve ? this.props.config.marginTwoAxis : this.props.config.margin;
        return d3.scaleLinear()
            .domain(this.yExtent).nice()
            .range([
                this.graphDiv.current.clientHeight - margins.bottom,
                margins.top
            ]);
    }

    /**
     * Y value scaling for cumulative values
     */
    yCumulative() {
        const margins = this.hasCumulativeCurve ? this.props.config.marginTwoAxis : this.props.config.margin;
        return d3.scaleLinear()
            .domain(this.yExtentCumulative).nice()
            .range([
                this.graphDiv.current.clientHeight - margins.bottom,
                margins.top
            ]);
    }

    xAxis(g: any) {
        const margins = this.hasCumulativeCurve ? this.props.config.marginTwoAxis : this.props.config.margin;
        const {timeSeries} = this.props;
        const period = timeSeries.periodISO || timeSeries.DEFAULT_PERIOD;
        const node = this.graphDiv.current;
        // x-axis is normally drawn on top, move to the bottom by transformation
        return g.attr("transform", `translate(0,${node.clientHeight - margins.bottom})`)
            .call(d3.axisBottom(this.x())  // show labels at the bottom of the axis
                // number of ticks (D3 decides about the optimal number of ticks)
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                .ticks(node.clientWidth / 100, this.props.config.axes.xAxis.tickFormats[period])
                .tickSizeOuter(0));
    }

    yAxis(g: any) {
        const margins = this.hasCumulativeCurve ? this.props.config.marginTwoAxis : this.props.config.margin;
        return g.attr("transform", `translate(${margins.left},0)`)
            .call(d3.axisLeft(this.y()))
            .call((node: any) => node.select(".domain").remove())
            .call((node: any) => node.select(".tick:last-of-type text").clone()
                .attr("x", 3)
                .attr("text-anchor", "start")
                .attr("font-weight", "bold")
                // eslint-disable-next-line max-len
                .text((data: [string, number, number]) => this.dataValueIndex === -1 ? null : data[this.dataValueIndex]));
    }

    yCumulativeAxis(g: any) {
        const margins = this.hasCumulativeCurve ? this.props.config.marginTwoAxis : this.props.config.margin;
        const node = this.graphDiv.current;
        return g.attr("transform", `translate(${node.clientWidth - margins.right},0)`)
            .call(d3.axisRight(this.yCumulative()))
            .call((n: any) => n.select(".domain").remove())
            .call((n: any) => n.select(".tick:first-of-type text").clone()
                .attr("x", 3)
                .attr("text-anchor", "start")
                .attr("font-weight", "bold")
                // eslint-disable-next-line max-len
                .text((data: [string, number, number]) => this.dataValueIndex === -1 ? null : data[this.dataValueIndex]));
    }

    callout(g: any, value: any) {
        if (!value) {
            return g.style("display", "none");
        }

        g.style("display", null)
            .style("pointer-events", "none")
            .style("font", "10px sans-serif");

        const path = g.selectAll("path")
            .data([null])
            .join("path");

        const text = g.selectAll("text")
            .data([null])
            .join("text")
            .call((txt: any) =>
                txt.selectAll("tspan")
                    .data((value + "").split(/\n/))
                    .join("tspan")
                    .attr("x", 0)
                    .attr("y", (d: any, i: any) => `${i * 1.1}em`)
                    .style("font-weight", (_: any, i: any) => i ? null : "bold")
                    .text((d: any) => d));

        const {y, width: w, height: h} = text.node().getBBox();

        text.attr("transform", `translate(${-w / 2},${15 - y})`);
        path.attr("d", `M${-w / 2 - 10},5H-5l5,-5l5,5H${w / 2 + 10}v${h + 20}h-${w + 20}z`);
    }

    // grid lines in x-axis function
    xGridLines(g: any) {
        const node = this.graphDiv.current;
        const margins = this.hasCumulativeCurve ? this.props.config.marginTwoAxis : this.props.config.margin;
        return g.attr("class", "axis-grid")
            .attr("transform", `translate(0,${node.clientHeight - margins.bottom})`)
            .call(d3.axisBottom(this.x())
                .ticks(node.clientWidth / 50)
                .tickSize(-node.clientHeight + margins.bottom + margins.top - 10)
                // @ts-ignore
                .tickFormat(""));
    }

    // grid lines in y-axis function
    yGridLines(g: any) {
        const margins = this.hasCumulativeCurve ? this.props.config.marginTwoAxis : this.props.config.margin;
        const node = this.graphDiv.current;
        return g.attr("class", "axis-grid")
            .attr("transform", `translate(${margins.left},0)`)
            .call(d3.axisLeft(this.y())
                .tickSize(-node.clientWidth + margins.left + margins.right)
                // @ts-ignore
                .tickFormat("")
            );
    }

    // grid lines in y cumulative axis function
    yCumulativeGridLines(g: any) {
        const margins = this.hasCumulativeCurve ? this.props.config.marginTwoAxis : this.props.config.margin;
        const node = this.graphDiv.current;
        return g.attr("class", "axis-grid-cumulative")
            .attr("transform", `translate(${node.clientWidth - margins.right},0)`)
            .call(d3.axisRight(this.yCumulative())
                .tickSize(-node.clientWidth + margins.left + margins.right)
                // @ts-ignore
                .tickFormat("")
            );
    }

    bisect(data: TimeSeriesDataTuple[], pos: number) {
        if (!data) {
            return null;
        }
        const dateAtMousePos = this.x().invert(pos);
        const index = this.bisectDate(data, dateAtMousePos, 1);
        const d0 = data[index - 1];
        const d1 = data[index];
        if (!d1) {
            return d0;
        }
        // @ts-ignore
        return (dateAtMousePos - new Date(d0[0])) > (new Date(d1[0]) - dateAtMousePos) ? d1 : d0;
    }

    barWidth(data: TimeSeriesDataTuple[]) {
        const node = this.graphDiv.current;
        let padding = 1;
        if (data.length <= 13) {
            padding = 8;
        } else if (data.length <= 32) {
            padding = 5;
        } else if (data.length < 170) {
            padding = 3;
        }
        else if (data.length < 340) {
            padding = 2;
        }
        const gapCount = data.length + 2;
        // get width not less than minValue and not greater than maxValue
        const minValue = 1;
        const maxValue = 20;
        return Math.max(minValue, Math.min(((node.clientWidth - (gapCount * padding)) / data.length), maxValue));
    }

    draw(data: TimeSeriesDataTuple[], node: BaseType, aspectRatio: number, loadingDate: Date | null | undefined,
         dataPointsVisible: boolean, dataValueIndex: 1 | 2, dataType: DataValueType,
         cumulativeData: TimeSeriesDataTuple[] | null,
         selectedThresholds: Array<string | number | boolean> | null | undefined,
         availableThresholds: (SelectOptionConfiguration)[] | null | undefined,
         thresholdValues: number[] | null | undefined, thresholdConfig: ThresholdsConfiguration | null) {
        const width = this.graphDiv.current.clientWidth;
        const height = this.graphDiv.current.clientHeight;

        if (width < 1 || height < 1) {
            // graph in a hidden tab
            return;
        }

        d3.select(node).select("svg").remove(); // remove previous graphs
        const svg = d3.select(node).append("svg")
            // .attr("width", width)
            // .attr("height", height)
            .attr("width", "100%")
            .attr("height", "100%")
            .attr("viewBox", this.viewBox())
            // .attr("preserveAspectRatio", "xMinYMin")
            .style("-webkit-tap-highlight-color", "transparent")
            .style("overflow", "visible");

        svg.append("g")
            .call(this.xAxis);

        svg.append("g")
            .call(this.yAxis);

        // Y axis label
        svg.append("text")
            .attr("class", "y-axis-label")
            .attr("text-anchor", "end")
            .attr("transform", "rotate(-90)")
            .attr("y", -4)
            .attr("x", -20)
            .text(this.yAxisLabel || "");

        // add the X grid lines
        svg.append("g")
            .call(this.xGridLines);

        // add the Y grid lines
        svg.append("g")
            .call(this.yGridLines);

        if (cumulativeData) {
            svg.append("g")
                .call(this.yCumulativeAxis);

            // Y cumulative axis label
            svg.append("text")
                .attr("class", "y-axis-label cumulative")
                .attr("text-anchor", "end")
                .attr("transform", "rotate(-90)")
                .attr("y", width)
                .attr("x", -20)
                .text(this.yCumulativeAxisLabel || "");

            // add the Y cumulative grid lines
            // svg.append("g")
            //     .call(this.yCumulativeGridLines);
        }

        // add threshold lines
        if (thresholdValues && thresholdConfig && dataType === DataValueType.Absolute) {
            if (thresholdConfig.stacked && thresholdConfig.plane) {
                this.stackedThresholdRibbons(svg, thresholdConfig, selectedThresholds, thresholdValues, data);
            } else {
                thresholdValues.forEach((tv, index) => {
                    if (!selectedThresholds || !selectedThresholds[index]) {
                        return;
                    }
                    const t = `${selectedThresholds[index]}`;
                    this.appendThreshold(svg, t, tv, data);
                });
            }
        }

        if (this.useBarChart) {
            // append the bar rectangles to the svg element
            const barWidth = this.barWidth(data);
            svg.selectAll()
                .data(data)
                .enter().append("rect")
                .attr("class", "data-bar")
                .attr("x", (d) => this.x()(Date.parse(d[0])) - (barWidth / 2))
                .attr("width", barWidth)
                .attr("y", (d) => this.y()(d[dataValueIndex]))
                .attr("height", (d) => {
                    const margins = this.hasCumulativeCurve ?
                        this.props.config.marginTwoAxis :
                        this.props.config.margin;
                    return this.graphDiv.current.clientHeight - margins.bottom - this.y()(d[dataValueIndex]);
                });
        } else {
            // append line for missing data values
            svg.append("path")
                // @ts-ignore
                .datum(data.filter(this.curve.defined()))
                .attr("class", "data-path-null-values")
                // @ts-ignore
                .attr("d", this.curve);

            // append line to svg element
            svg.append("path")
                .datum(data)
                .attr("class", "data-path")
                // @ts-ignore
                .attr("d", this.curve);
        }

        if (cumulativeData) {
            // append line for missing data values
            svg.append("path")
                // @ts-ignore
                .datum(cumulativeData.filter(this.lineCumulative.defined()))
                .attr("class", "data-path-null-values")
                // @ts-ignore
                .attr("d", this.lineCumulative);

            // append line to svg element
            svg.append("path")
                .datum(cumulativeData)
                .attr("class", "data-path-cumulative")
                // @ts-ignore
                .attr("d", this.lineCumulative);
        }

        if (dataPointsVisible) {
            svg.selectAll("circle").data(data).enter().append("circle")
                .attr("cx", (d) => {
                    return this.x()(new Date(d[0]));
                })
                .attr("cy", (d) => {
                    return this.y()(d[dataValueIndex]);
                })
                .attr("r", 6)
                .attr("class", "data-point");
        }

        // tooltip graph
        const tooltip = svg.append("g");
        const focusRadius = 6;
        // circle to highlight selected data point
        const focus = svg
            .append("g")
            .append("circle")
            .attr("class", "data-focus")
            .attr("r", focusRadius)
            .style("opacity", 0);
        // add reference lines to point to x and y axis
        const pointerLineX = svg.append("line")
            .classed("x", true)
            .attr("class", "pointer-line pointer-line-x");
        const pointerLineY = svg.append("line")
            .classed("y", true)
            .attr("class", "pointer-line pointer-line-y");

        svg.on("mouseover", () => {
            focus.style("opacity", 1);
        });

        svg.on("touchmove mousemove", (event) => {
            const mouseX = event.offsetX;
            const margins = this.hasCumulativeCurve ? this.props.config.marginTwoAxis : this.props.config.margin;

            const d = this.bisect(data, mouseX);
            if (!d || !d[0]) {
                return;
            }

            const date = new Date(d[0]);
            let label;
            if (isNaN(d[dataValueIndex])) {
                label = "kein Wert";
            } else {
                const value = d[dataValueIndex];
                const calloutHeight = 61;
                const valueFormatted = value.toLocaleString("de-CH", { style: "decimal", maximumFractionDigits: 3});
                const unitLabel = UnitsUtils.unitLabel(this.props.timeSeries.unitSymbol, dataType);
                const formattedTime = this.formattedTime(this.props.timeSeries, date);
                label = `${valueFormatted} ${unitLabel}
                    ${formattedTime}`;

                // position highlight circle
                focus
                    .attr("cx", this.x()(date))
                    .attr("cy", this.y()(value));
                // position reference lines
                pointerLineX
                    .attr("x1", margins.left)
                    .attr("x2", this.x()(date))
                    .attr("y1", this.y()(value))
                    .attr("y2", this.y()(value));
                pointerLineY
                    .attr("x1", this.x()(date))
                    .attr("x2", this.x()(date))
                    .attr("y1", () => {
                        const y = this.y()(value);
                        const calloutSize = focusRadius + calloutHeight;
                        if ((y - this.graphDiv.current.clientHeight - margins.bottom) > calloutSize) {
                            // fix cases, when value is less than calloutSize --> avoid painting line below x axis
                            return y + calloutSize;
                        }
                        return y;
                    })
                    .attr("y2", this.graphDiv.current.clientHeight - margins.bottom);
            }

            tooltip
                .attr("class", "chart-tooltip")
                .attr("transform", `translate(${this.x()(date)},${this.y()(d[dataValueIndex]) + 15})`)
                .call(this.callout, label);
        });

        svg.on("touchend mouseleave", () => {
            tooltip.call(this.callout, null);
            focus.style("opacity", 0);
        });

        // remove loader
        setTimeout(() =>
            d3.select(node).select("div").remove()
        , 300);

        return svg.node();
    }

    componentDidMount() {
        this.disposeRedrawOnDataChange = autorun(() => {
            // load time series list for this station,
            // results will be displayed as soon as available
            if (this.props.visible) {
                const dataValueIndex = this.dataValueIndex;
                if (dataValueIndex === -1) {
                    // data index not available yet -> return until index is present
                    return;
                }
                const div = this.graphDiv.current;

                const dataType = this.showRelativeData ? DataValueType.Relative : DataValueType.Absolute;
                const data = this.timeSeriesData;
                const cumulativeData = this.cumulativeData(data, dataValueIndex);

                const { dataPointsVisible } = this.state;
                // aspect ratio triggers a redraw, when popup was resized
                const { kiwis, timeSeries, aspectRatio, config } = this.props;
                const loadingDate = kiwis.timeSeriesDataTimestamps.get(timeSeries.id);

                // thresholds
                const thresholdValues = kiwis.selectedThresholdValues.get(timeSeries.id);
                const thresholdConfig = this.thresholdConfig(kiwis, timeSeries, config);
                const selectedThresholds = kiwis.selectedThresholds.get(timeSeries.id);
                const availableThresholds = kiwis.availableThresholds.get(timeSeries.id);

                setTimeout(() => {
                    if (!div) {
                        return;
                    }
                    this.draw(
                        data,
                        div,
                        aspectRatio,
                        loadingDate,
                        dataPointsVisible,
                        dataValueIndex,
                        dataType,
                        cumulativeData,
                        selectedThresholds,
                        availableThresholds,
                        thresholdValues,
                        thresholdConfig
                    );
                }, 300);
            }
        }, { delay: 300, name: "redraw-chart" });
    }

    componentWillUnmount() {
        // TODO remove watches
        this.disposeRedrawOnDataChange();
    }

    render() {
        const {timeSeries, kiwis, config} = this.props;
        if (!config) {
            return null;
        }
        if (kiwis.generalisationStatus === GeneralisationStatus.Error) {
            return (
                <Message
                    className="chart-error-message"
                    icon="bug"
                    header="Fehler"
                    content="Fehler bei Generalisierung der Daten." />
            );
        }
        if (!timeSeries.coverage ||
            !timeSeries.coverage.from ||
            !timeSeries.coverage.to) {
            return (
                <div>
                    <Message
                        className="chart-no-data-message"
                        icon="eye slash outline"
                        header="Keine Daten verfügbar"
                        content="Keine Messwerte für diesen Parameter in Wiski vorhanden." />
                    <RefreshButton kiwis={this.props.kiwis} timeSeries={this.props.timeSeries} />
                </div>
            );
        }
        if (this.timeSeriesData != null && this.timeSeriesData.length >= 1) {
            const {dataPointsVisible} = this.state;
            const {parentWidth} = this.props;
            const selectableTimePeriods = config.periods.filter((p) => {
                return p.stationParameterNames.includes(timeSeries.stationParameterName);
            })[0].options;
            const defaultPeriod = timeSeries.periodISO; // use default period from time series as default
            const measurementLabel = this.currentMeasurementLabel(this.timeSeriesData, timeSeries);
            const isGeneralizing = kiwis.generalisationStatus === GeneralisationStatus.Calculating;
            const isLoading =
                kiwis.status.length &&
                kiwis.status[kiwis.status.length - 1].type === StatusType.Loading;
            const showLoader = isGeneralizing || isLoading || false;

            const thresholdConfig = this.thresholdConfig(kiwis, timeSeries, config);

            return (
                <div className="graph-container">
                    <Transition visible={showLoader} animation="fade" duration={500}>
                        <Dimmer active={showLoader}
                            inverted className="generalisation-status" >
                            <Loader size="medium" className="chart-data-loader">
                                {
                                    isLoading ? "Daten laden..." :
                                        isGeneralizing ? kiwis.generalisationStatus : ""
                                }
                            </Loader>
                        </Dimmer>
                    </Transition>
                    {thresholdConfig && !this.showRelativeData &&
                        <Thresholds
                            kiwis={kiwis}
                            timeSeries={timeSeries}
                            compact={config.rangeSelector.onlyPeriod}
                            config={thresholdConfig}
                        />
                    }
                    {this.props.parentWidth > 980 &&
                        <div className="graph-ribbon">
                            <Label as="a" color="teal"
                                ribbon="right" className="time-series-coverage-ribbon">
                                {this.dataCoverageLabel}
                            </Label>
                        </div>
                    }
                    <div ref={this.graphDiv} className="graph">
                        <Dimmer active inverted>
                            <Loader>wird geladen</Loader>
                        </Dimmer>
                    </div>
                    <ChartTools kiwis={kiwis}
                        timeSeries={timeSeries}
                        dataPointsVisible={dataPointsVisible}
                        chartContentLabel={this.chartContentLabel}
                        currentMeasurementLabel={measurementLabel}
                        dateRangeLabel={this.dateRangeLabel}
                        toggleDataPointVisibility={this.toggleDataPointVisibility}
                        compact={config.rangeSelector.onlyPeriod}
                    />
                    <TimeSeriesRangeSelector
                        periods={selectableTimePeriods}
                        defaultPeriod={defaultPeriod}
                        timeSeries={timeSeries}
                        parentWidth={parentWidth}
                        config={config.rangeSelector}
                    />
                </div>
            );
        } else {
            return (
                <div>
                    <Message
                        className="chart-no-data-message">
                        <Icon name="folder outline" />
                        <Message.Header>Keine Daten verfügbar</Message.Header>
                        <Message.Content>
                            Für die Zeitperiode stehen für diesen Parameter keine Daten zur Verfügung
                        </Message.Content>
                        <Label as="a" size="tiny" attached="bottom right" className="geo-chart-period-label">
                            <Icon name="calendar alternate outline" />
                            {this.dateRangeLabel}
                        </Label>
                    </Message>
                    <RefreshButton kiwis={this.props.kiwis} timeSeries={this.props.timeSeries} />
                </div>
            );
        }
    }

    /**
     * Calculate cumulative data value and add it as value to time series data tuple
     *
     * @param data
     * @param dataValueIndex
     */
    private cumulativeData(data: TimeSeriesDataTuple[], dataValueIndex: 1 | 2): TimeSeriesDataTuple[] | null {
        if (!this.hasCumulativeCurve) {
            return null;
        }

        const cumulativeData: TimeSeriesDataTuple[] = [];
        data.forEach((tuple, i) => {
            if (i === 0) {
                const v0 = tuple[dataValueIndex];
                cumulativeData.push([tuple[0], v0, v0]);
            } else {
                const v = cumulativeData[i - 1][dataValueIndex] + tuple[dataValueIndex];
                cumulativeData.push([tuple[0], v, v]);
            }
        });
        return cumulativeData;
    }

    /**
     * Format date time based on configuration.
     * Formatter depends on period and parameter type
     *
     * @param ts: TimeSeries
     * @param time: Date time
     */
    private formattedTime(ts: TimeSeries, time: Date) {
        const period = ts.periodISO || ts.DEFAULT_PERIOD;

        const formatConfig = this.props.config.timeFormats.filter((f) => {
            return f.stationParameterNames.includes(ts.stationParameterName);
        })[0];
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const formatter = formatConfig.formats[period] || "DD.MM.YYYY HH:mm";

        return dayjs(time).format(formatter);
    }

    /**
     * Offset the X coordinate date extent
     * e.g. to avoid bars overlapping chart boundaries
     *
     * @param extent
     */
    private offsetDateExtent(extent: Date[]) {
        // offset x extent to avoid bars overlapping the chart boundaries
        // @ts-ignore
        const intervalInDays = parseInt((extent[1] - extent[0]) / (24 * 3600 * 1000), 10);

        // offset in hours
        let offset = 0;
        if (intervalInDays <= 2) {
            offset = 1;
        }
        else if (intervalInDays <= 7) {
            offset = 5;
        }
        else if (intervalInDays <= 31) {
            offset = 18;
        }
        else if (intervalInDays <= 366) {
            offset = 7 * 24; // 1 week
        }
        else if (intervalInDays <= 732) {
            offset = 4 * 7 * 24; // 1 month
        }
        // set offset (in hours)
        return [d3.timeHour.offset(extent[0], -(offset)), d3.timeHour.offset(extent[1], offset)];
    }

    /**
     * Get the threshold configuration block
     *
     * @param kiwis
     * @param timeSeries
     * @param config
     */
    private thresholdConfig(kiwis: Kiwis, timeSeries: TimeSeries, config: ChartConfiguration) {
        if (!kiwis.selectedStationId.get()) {
            return null;
        }
        const station = kiwis.stations.get(kiwis.selectedStationId.get());
        if (!station) {
            return null;
        }
        return config.thresholds.filter((f) => {
            return f.parameterTypes.includes(`${station.baseId}-${timeSeries.parameterTypeName}`);
        })[0];
    }

    /**
     * Append a straight line for threshold values
     *
     * @param svg
     * @param threshold
     * @param thresholdValue
     * @param data
     */
    private appendThreshold(svg: any, threshold: string, thresholdValue: number, data: TimeSeriesDataTuple[]) {
        if (!svg || !threshold || !data) {
            return;
        }

        svg.append("path")
            .datum([[data[0][0], thresholdValue], [data[data.length - 1][0], thresholdValue]])
            .attr("class", `data-path-threshold ${threshold}`)
            .attr("d", this.lineStraight);
    }

    /**
     * Add thresholds as stacked areas to the graph
     *
     * @param svg
     * @param thresholds
     * @param thresholdValues
     * @param thresholdConfig
     * @param data
     */
    private stackedThresholdRibbons(svg: any,
                                    thresholdConfig: ThresholdsConfiguration | null,
                                    thresholds: Array<string | number | boolean> | null | undefined,
                                    thresholdValues: number[] | null | undefined,
                                    data: TimeSeriesDataTuple[]) {

        if (!thresholds || !thresholdValues || !thresholdValues.length ||
            !thresholdConfig || !thresholdConfig.values || !thresholdConfig.values.length) {
            return;
        }

        // add min value
        const minDate = Date.parse(data[0][0]);
        const maxDate = Date.parse(data[data.length - 1][0]);


        // compile data array with borders of each area
        // e.g.
        // data = [
        //   [ key: "min", index: 0, 0: [0, 0, date: "2020-01-01T14:00"], 1: [0, 0, date: "2020-02-28T14:00"] ],
        //   [ key: "soilWet", index: 1, 0: [0, 6, date: "2020-01-01T14:00"], 1: [0, 6, date: "2020-02-28T14:00"] ],
        //   [ key: "max", index: 2, 0: [6, 100, date: "2020-01-01T14:00"], 1: [6, 100, date: "2020-02-28T14:00"] ]
        // ]
        const thresholdData: any[] = [];

        // add threshold values, respect order of the config to avoid threshold overlaps
        // (selected thresholds are unordered)
        thresholdConfig.values.forEach((tcv, index) => {
            // Is this threshold selected? or min/max
            if(tcv.type !== "min" && tcv.type !== "max" && thresholds.indexOf(tcv.value) === -1) {
                return;
            }

            let prevStop = 0;
            if (index === 0) {
                prevStop = thresholdConfig.values[0].stop;
            } else if (index > 0) {
                prevStop = thresholdConfig.values[index - 1].stop;
            }

            const stop = tcv.stop;

            const item0 = [prevStop, stop];
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            item0["date"] = minDate;
            const item1 = [prevStop, stop];
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            item1["date"] = maxDate;
            const item = [item0, item1];
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            item["key"] = tcv.value;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            item["index"] = index;

            thresholdData.push(item);
        });

        const area = d3.area()
            // @ts-ignore
            .x((d) => this.x()(d.date))
            .y0((d) => this.y()(d[0]))
            .y1((d) => this.y()(d[1]));

        svg.append("g")
            .selectAll("path")
            .data(thresholdData)
            .join("path")
            // @ts-ignore
            .attr("class", ({key}) => `data-area-threshold ${key}`)
            .attr("d", area)
            .append("title")
            // @ts-ignore
            .text(({key}) => thresholdConfig.values.filter((v) => v.value === key)[0].text);
    }

    /**
     * Get min max threshold values
     * Calculated from min selected value and min value in configuration
     */
    getMinMaxThresholds() {
        const {kiwis, config, timeSeries} = this.props;

        // respect thresholds
        const minSelected = kiwis.selectedMinThresholdValue.get(timeSeries.id);
        let minStop = minSelected;
        const maxSelected = kiwis.selectedMaxThresholdValue.get(timeSeries.id);
        let maxStop = maxSelected;

        // respect specified fixed min/max values
        const tc = this.thresholdConfig(kiwis, timeSeries, config);
        if (tc) {
            const minConfig = tc.values.filter((v) => v.type === "min")[0];
            if (minConfig && minConfig.stop !== undefined && minConfig.stop !== null) {
                minStop = minConfig.stop;
            }
            const maxConfig = tc.values.filter((v) => v.type === "max")[0];
            if (maxConfig && maxConfig.stop !== undefined && minConfig.stop !== null) {
                maxStop = maxConfig.stop;
            }
        }

        const min = minSelected && minStop ?  Math.min(minSelected, minStop) : minStop;
        const max = maxSelected && maxStop ?  Math.max(maxSelected, maxStop) : maxStop;

        return {min, max};
    }

}
